// @djsem/shared — Smart Groups (Prompt 06).
//
// Dynamic saved VIEWS over the DJSEM track library. A Smart Group is a set of rules
// evaluated against normalized DJSEMLibraryTrack view objects — it never moves, copies,
// or mutates track data (it is a filter, not a crate). Prep/library side only: the live
// runtime may later consume an approved *set*, never Smart Group logic (product boundary).
//
// DJSEMLibraryTrack is a flat, normalized projection so no module needs the full analysis
// package just to be filterable (derive it from metadata intelligence + live readiness +
// cues + chapters + stem/rights info).

import {
  vArray, vObject, vString, vUnion, type Validator,
  type ValidationResult, type DJSEMValidationResult, toDJSEMValidationResult,
} from "./validation";

// ---- contracts --------------------------------------------------------------

/** Comparison operator a Smart Group rule evaluates a field with. */
export type DJSEMSmartGroupRuleOperator =
  | "equals" | "not_equals" | "contains" | "not_contains"
  | "greater_than" | "less_than" | "between" | "exists" | "missing";

/** A queryable field on a {@link DJSEMLibraryTrack} that a Smart Group rule may filter on. */
export type DJSEMSmartGroupField =
  | "approvedForLive" | "reviewStatus" | "primaryGenre" | "subgenre" | "moodTag"
  | "energyScore" | "energyLabel" | "bpm" | "key" | "semanticDomain"
  | "semanticCategory" | "chapterType" | "cueIntent" | "hasWarnings" | "hasFallbacks"
  | "stemAvailable" | "rightsStatus" | "setMembership";

/** All valid {@link DJSEMSmartGroupField} values. */
export const SMART_GROUP_FIELDS: DJSEMSmartGroupField[] = [
  "approvedForLive", "reviewStatus", "primaryGenre", "subgenre", "moodTag",
  "energyScore", "energyLabel", "bpm", "key", "semanticDomain", "semanticCategory",
  "chapterType", "cueIntent", "hasWarnings", "hasFallbacks", "stemAvailable",
  "rightsStatus", "setMembership",
];

/** All valid {@link DJSEMSmartGroupRuleOperator} values. */
export const SMART_GROUP_OPERATORS: DJSEMSmartGroupRuleOperator[] = [
  "equals", "not_equals", "contains", "not_contains",
  "greater_than", "less_than", "between", "exists", "missing",
];

/** A single filter condition within a Smart Group. */
export interface DJSEMSmartGroupRule {
  id: string;
  field: DJSEMSmartGroupField;
  operator: DJSEMSmartGroupRuleOperator;
  value?: unknown;
  values?: unknown[];
}

/** How a Smart Group's matched tracks should be ordered. */
export type DJSEMSmartGroupSortMode =
  | "manual" | "title" | "artist" | "bpm_ascending" | "bpm_descending"
  | "energy_ascending" | "energy_descending" | "semantic_similarity"
  | "chapter_fit" | "live_readiness";

/** A dynamic saved view (filter) over the track library — never mutates or moves tracks. */
export interface DJSEMSmartGroup {
  id: string;
  label: string;
  description?: string;
  rules: DJSEMSmartGroupRule[];
  ruleMode: "all" | "any";
  sortMode: DJSEMSmartGroupSortMode;
  createdAt: string;
  updatedAt: string;
  source: "system" | "user";
}

/**
 * Normalized, flat view of a track for filtering. Scalar OR array facets; arrays support
 * `contains`. All fields optional — missing data must never crash evaluation.
 */
export interface DJSEMLibraryTrack {
  preparationTrackId: string;
  title?: string;
  artist?: string;
  approvedForLive?: boolean;
  reviewStatus?: string;
  primaryGenre?: string;
  subgenres?: string[];
  moodTags?: string[];
  energyScore?: number;
  energyLabel?: string;
  bpm?: number;
  key?: string;
  semanticDomains?: string[];
  semanticCategories?: string[];
  chapterTypes?: string[];
  cueIntents?: string[];
  hasWarnings?: boolean;
  hasFallbacks?: boolean;
  stemAvailable?: boolean;
  rightsStatus?: string;
  setMemberships?: string[];
}

/** Result of evaluating a Smart Group against a track library. */
export interface DJSEMSmartGroupResult {
  groupId: string;
  trackIds: string[];
  tracks: DJSEMLibraryTrack[];
  matchCount: number;
}

// Which DJSEMLibraryTrack property backs each queryable field, and whether it's an array.
const FIELD_MAP: Record<DJSEMSmartGroupField, { key: keyof DJSEMLibraryTrack; array: boolean }> = {
  approvedForLive: { key: "approvedForLive", array: false },
  reviewStatus: { key: "reviewStatus", array: false },
  primaryGenre: { key: "primaryGenre", array: false },
  subgenre: { key: "subgenres", array: true },
  moodTag: { key: "moodTags", array: true },
  energyScore: { key: "energyScore", array: false },
  energyLabel: { key: "energyLabel", array: false },
  bpm: { key: "bpm", array: false },
  key: { key: "key", array: false },
  semanticDomain: { key: "semanticDomains", array: true },
  semanticCategory: { key: "semanticCategories", array: true },
  chapterType: { key: "chapterTypes", array: true },
  cueIntent: { key: "cueIntents", array: true },
  hasWarnings: { key: "hasWarnings", array: false },
  hasFallbacks: { key: "hasFallbacks", array: false },
  stemAvailable: { key: "stemAvailable", array: false },
  rightsStatus: { key: "rightsStatus", array: false },
  setMembership: { key: "setMemberships", array: true },
};

// ---- validator (pure) -------------------------------------------------------

const vOperator: Validator = vUnion(...SMART_GROUP_OPERATORS.map((o) => vString({ enum: [o] })));
const vField: Validator = vUnion(...SMART_GROUP_FIELDS.map((f) => vString({ enum: [f] })));

/** Runtime validator for {@link DJSEMSmartGroupRule}. */
export const vSmartGroupRule: Validator = vObject(
  { id: vString({ min: 1 }), field: vField, operator: vOperator },
  { value: (v) => ({ valid: true, issues: [] }), values: vArray((v) => ({ valid: true, issues: [] })) },
);

/** Runtime validator for {@link DJSEMSmartGroup}. */
export const vSmartGroup: Validator<DJSEMSmartGroup> = vObject(
  {
    id: vString({ min: 1 }),
    label: vString({ min: 1 }),
    rules: vArray(vSmartGroupRule),
    ruleMode: vUnion(vString({ enum: ["all"] }), vString({ enum: ["any"] })),
    sortMode: vUnion(...(["manual", "title", "artist", "bpm_ascending", "bpm_descending",
      "energy_ascending", "energy_descending", "semantic_similarity", "chapter_fit",
      "live_readiness"] as const).map((s) => vString({ enum: [s] }))),
    createdAt: vString({ min: 1 }),
    updatedAt: vString({ min: 1 }),
    source: vUnion(vString({ enum: ["system"] }), vString({ enum: ["user"] })),
  },
  { description: vString({}) },
) as Validator<DJSEMSmartGroup>;

// ---- service (class-based; hard rule 12) ------------------------------------

/** Evaluates, sorts, and validates Smart Groups (dynamic saved filters) against a track library. */
export class SmartGroupService {
  /**
   * Filter and sort a track library by a Smart Group's rules and sort mode.
   * @param group - The Smart Group to evaluate.
   * @param tracks - The library tracks to filter.
   * @returns The matched (and sorted) track ids/tracks with a match count.
   */
  evaluateGroup(group: DJSEMSmartGroup, tracks: DJSEMLibraryTrack[]): DJSEMSmartGroupResult {
    const matched = tracks.filter((t) => this.trackMatchesGroup(group, t));
    const sorted = this.sortGroupTracks(matched, group.sortMode);
    return {
      groupId: group.id,
      trackIds: sorted.map((t) => t.preparationTrackId),
      tracks: sorted,
      matchCount: sorted.length,
    };
  }

  /**
   * Whether a single track matches all (or any) of a Smart Group's rules.
   * @param group - The Smart Group whose rules and rule mode ("all"/"any") apply.
   * @param track - The track to test.
   * @returns True if the track matches; an empty rule set matches every track.
   */
  trackMatchesGroup(group: DJSEMSmartGroup, track: DJSEMLibraryTrack): boolean {
    if (group.rules.length === 0) return true; // empty group = all tracks
    const results = group.rules.map((r) => this.evaluateRule(r, track));
    return group.ruleMode === "all" ? results.every(Boolean) : results.some(Boolean);
  }

  /**
   * Evaluate a single rule against a track. Never throws; missing values are false
   * except for the `missing` operator, which is true when the field is absent.
   * @param rule - The rule to evaluate.
   * @param track - The track to test.
   * @returns Whether the rule matches.
   */
  evaluateRule(rule: DJSEMSmartGroupRule, track: DJSEMLibraryTrack): boolean {
    const map = FIELD_MAP[rule.field];
    if (!map) return false; // unknown field never matches (validation catches it separately)
    const raw = track[map.key];
    const present = raw !== undefined && raw !== null && (!Array.isArray(raw) || raw.length > 0);

    switch (rule.operator) {
      case "exists": return present;
      case "missing": return !present;
      default: break;
    }
    if (!present) return false; // all other operators require a value

    if (map.array) {
      const arr = (raw as unknown[]).map(String);
      switch (rule.operator) {
        case "contains": return arr.includes(String(rule.value));
        case "not_contains": return !arr.includes(String(rule.value));
        case "equals": return arr.includes(String(rule.value));
        case "not_equals": return !arr.includes(String(rule.value));
        default: return false; // numeric ops don't apply to arrays
      }
    }

    // scalar
    switch (rule.operator) {
      case "equals": return String(raw) === String(rule.value);
      case "not_equals": return String(raw) !== String(rule.value);
      case "contains": return String(raw).toLowerCase().includes(String(rule.value).toLowerCase());
      case "not_contains": return !String(raw).toLowerCase().includes(String(rule.value).toLowerCase());
      case "greater_than": return this.num(raw) !== null && this.num(rule.value) !== null && (this.num(raw)! > this.num(rule.value)!);
      case "less_than": return this.num(raw) !== null && this.num(rule.value) !== null && (this.num(raw)! < this.num(rule.value)!);
      case "between": {
        const n = this.num(raw); const lo = this.num(rule.values?.[0]); const hi = this.num(rule.values?.[1]);
        return n !== null && lo !== null && hi !== null && n >= lo && n <= hi;
      }
      default: return false;
    }
  }

  /**
   * Sort tracks according to a Smart Group's sort mode. `semantic_similarity`,
   * `chapter_fit`, and `manual` are stable pass-throughs (no reference track available here).
   * @param tracks - The tracks to sort.
   * @param sortMode - The sort mode to apply.
   * @returns A new, sorted array (input is not mutated).
   */
  sortGroupTracks(tracks: DJSEMLibraryTrack[], sortMode: DJSEMSmartGroupSortMode): DJSEMLibraryTrack[] {
    const xs = [...tracks];
    const byStr = (f: (t: DJSEMLibraryTrack) => string) => (a: DJSEMLibraryTrack, b: DJSEMLibraryTrack) => f(a).localeCompare(f(b));
    const byNum = (f: (t: DJSEMLibraryTrack) => number, dir: 1 | -1) => (a: DJSEMLibraryTrack, b: DJSEMLibraryTrack) => (f(a) - f(b)) * dir;
    switch (sortMode) {
      case "title": return xs.sort(byStr((t) => t.title ?? ""));
      case "artist": return xs.sort(byStr((t) => t.artist ?? ""));
      case "bpm_ascending": return xs.sort(byNum((t) => t.bpm ?? 0, 1));
      case "bpm_descending": return xs.sort(byNum((t) => t.bpm ?? 0, -1));
      case "energy_ascending": return xs.sort(byNum((t) => t.energyScore ?? 0, 1));
      case "energy_descending": return xs.sort(byNum((t) => t.energyScore ?? 0, -1));
      case "live_readiness": return xs.sort(byNum((t) => (t.approvedForLive ? 1 : 0), -1));
      // semantic_similarity / chapter_fit need a reference track; deterministic stable pass-through here.
      case "semantic_similarity":
      case "chapter_fit":
      case "manual":
      default:
        return xs; // stable: preserves input order (manual belongs to Set Builder)
    }
  }

  /**
   * Validate a Smart Group's shape, additionally rejecting unknown fields/operators.
   * @param group - The candidate Smart Group to validate.
   * @returns The validation result.
   */
  validateGroup(group: unknown): DJSEMValidationResult {
    const base: ValidationResult = vSmartGroup(group);
    // Extra: reject unknown fields / operators explicitly (belt-and-braces over the enum validator).
    const issues = [...base.issues];
    const g = group as DJSEMSmartGroup;
    if (g && Array.isArray(g.rules)) {
      g.rules.forEach((r, i) => {
        if (r && !SMART_GROUP_FIELDS.includes(r.field)) issues.push({ path: `$.rules[${i}].field`, message: `unknown field: ${String(r.field)}` });
        if (r && !SMART_GROUP_OPERATORS.includes(r.operator)) issues.push({ path: `$.rules[${i}].operator`, message: `unknown operator: ${String(r.operator)}` });
      });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "smart_group");
  }

  private num(v: unknown): number | null {
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
    return null;
  }
}

// ---- system smart groups ----------------------------------------------------

const T0 = "1970-01-01T00:00:00.000Z";
const sys = (id: string, label: string, description: string, ruleMode: "all" | "any",
  sortMode: DJSEMSmartGroupSortMode, rules: Omit<DJSEMSmartGroupRule, "id">[]): DJSEMSmartGroup => ({
  id, label, description, ruleMode, sortMode, source: "system", createdAt: T0, updatedAt: T0,
  rules: rules.map((r, i) => ({ id: `${id}_r${i}`, ...r })),
});

/** Built-in, system-authored Smart Groups shipped by default (needs review, approved for live, etc.). */
export const SYSTEM_SMART_GROUPS: DJSEMSmartGroup[] = [
  sys("sys_needs_review", "Needs Review", "Not yet reviewed or flagged for review.", "any", "live_readiness", [
    { field: "reviewStatus", operator: "equals", value: "not_reviewed" },
    { field: "reviewStatus", operator: "equals", value: "needs_review" },
  ]),
  sys("sys_approved_for_live", "Approved For Live", "Approved and ready to perform.", "all", "live_readiness", [
    { field: "approvedForLive", operator: "equals", value: true },
  ]),
  sys("sys_fallback_low_conf", "Fallback / Low Confidence", "Used a fallback or has low-confidence metadata.", "any", "title", [
    { field: "hasFallbacks", operator: "equals", value: true },
  ]),
  sys("sys_peak_energy", "Peak Energy Tracks", "Peak-energy material.", "any", "energy_descending", [
    { field: "energyLabel", operator: "equals", value: "peak" },
    { field: "energyScore", operator: "greater_than", value: 0.85 },
  ]),
  sys("sys_build_tracks", "Build Tracks", "Tracks whose structure suggests a build.", "any", "energy_ascending", [
    { field: "chapterType", operator: "contains", value: "build" },
    { field: "semanticCategory", operator: "contains", value: "build" },
  ]),
  sys("sys_drop_impact", "Drop / Impact Tracks", "Tracks with drop or impact cues.", "any", "energy_descending", [
    { field: "cueIntent", operator: "contains", value: "drop" },
    { field: "cueIntent", operator: "contains", value: "impact" },
  ]),
  sys("sys_vocal", "Vocal Tracks", "Vocals or lyrics available.", "any", "title", [
    { field: "semanticCategory", operator: "contains", value: "vocal" },
    { field: "cueIntent", operator: "contains", value: "vocal_entry" },
  ]),
  sys("sys_instrumental", "Instrumental / No Lyrics", "No vocals detected.", "all", "title", [
    { field: "semanticCategory", operator: "not_contains", value: "vocal" },
  ]),
  sys("sys_stem_ready", "Stem-Ready Tracks", "Stem metadata available.", "all", "title", [
    { field: "stemAvailable", operator: "equals", value: true },
  ]),
  sys("sys_rights_warning", "Rights Warning", "Rights unknown or restricted.", "any", "title", [
    { field: "rightsStatus", operator: "equals", value: "unknown" },
    { field: "rightsStatus", operator: "equals", value: "restricted" },
  ]),
];
